import React from 'react';
import { Cog8ToothIcon } from '@heroicons/react/24/solid';
import { MdCircle } from 'react-icons/md';
import modelProviderMapping from "../../../../../../../Utils/IdMappings/modelProviderMapping.ts";
import {OverlayWrapper} from "../../../../../../OverlayWrapper.tsx";
import {useAppStore} from "../../../../../../../GlobalStore/GlobalStore.ts";
import {modelOptions} from "../../ModelsDropdown.tsx";
import {isMobileApp} from "../../../../../../../Utils/misc/envNameUtils.ts";

const getModelPricing = (modelName: string): string => {
    const provider = modelProviderMapping[modelName];

    if (provider === 'Anthropic') {
        switch (modelName) {
            case 'Claude-Fable':
                return 'Input $10/1M | Output $50/1M';
            case 'Claude-Haiku':
                return 'Input $1/1M | Output $5/1M';
            case 'Claude-Sonnet':
                return 'Input $2/1M | Output $10/1M';
            case 'Claude-Opus':
                return 'Input $5/1M | Output $25/1M';
            default:
                return '';
        }
    } else if (provider === 'OpenAI') {
        switch (modelName) {
            case 'GPT-Mini':
                return 'Free';
            case 'GPT':
                return 'Input $5/1M | Output $30/1M';
            default:
                return '';
        }
    } else if (provider === 'Google') {
        switch (modelName) {
            case 'Gemini-Pro':
                return 'Input $2/1M | Output $12/1M';
            case 'Gemini-Flash':
                return 'Free';
            default:
                return '';
        }
    } else if (provider === 'xAI') {
        switch (modelName) {
            case 'Grok':
                return 'Input $1.25/1M | Output $2.50/1M';
            default:
                return '';
        }
    } else if (provider === 'Other') {
        switch (modelName) {
            case 'GLM':
                return 'Free';
            default:
                return '';
        }
    }

    return '';
};

const getModelConfig = (modelName: string) => {
    for (const provider of modelOptions) {
        const model = provider.models.find(m => m.name === modelName);
        if (model && model.config) {
            return model.config;
        }
    }
    return null;
};

const hasModelConfig = (modelName: string): boolean => {
    return false;
    return getModelConfig(modelName) !== null;
};

interface ModelModelItemProps {
    modelName: string;
    isPremium: boolean;
    handleModelSelect: (model: string) => void;
    setIsOpen: (isOpen: boolean) => void;
}

const ModelItem: React.FC<ModelModelItemProps> = ({ modelName, isPremium, handleModelSelect, setIsOpen }) => {
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const { environment } = environmentObj;

    const modelPricing = getModelPricing(modelName);
    const showSettings = hasModelConfig(modelName);

    const handleSettingsClick = (e: any) => {
        e.stopPropagation();
        handleModelSelect(modelName);
        setDynamicStateObj({
            visiblePopup: `modelConfig:${modelName}`
        });
        setIsOpen(false);
    };

    const button = (
        <button
            className={`w-full flex justify-between items-center px-4 py-2 text-left text-sm text-black dark:text-white hover:bg-neutral-200 dark:hover:bg-neutral-800 border-t border-neutral-300 dark:border-neutral-700`}
            onClick={() => handleModelSelect(modelName)}
            data-tooltip-id="main-tooltip"
            data-tooltip-html={`${isMobileApp(environment) ? "" : modelPricing}`}
            data-tooltip-place="right-start"
        >
            <span>{modelName}</span>
            <div className="flex items-center">
                {settingsObj.model === modelName && <MdCircle size={11} className="text-cyan-600 dark:text-cyan-500" />}
                {showSettings && (
                    <Cog8ToothIcon className="h-5 w-5 text-neutral-700 dark:text-gray-200 ml-2" onClick={handleSettingsClick}/>
                )}
            </div>
        </button>
    );

    // In API key mode, usage-based pricing restrictions don't apply
    if (settingsObj.apiKeyOnly) {
        return button;
    }

    // Normal mode: apply usage-based pricing restrictions for premium models
    return isPremium ? (
        <OverlayWrapper type={"usageBasedDisabled"} toolTipDirection={"top-left"}>
            {button}
        </OverlayWrapper>
    ) : button;
};

export default ModelItem;
